import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
    Phone, Mail, StickyNote, Users, MessageCircle, ArrowRightCircle, UserCheck,
    ChevronDown, ChevronRight, Clock, Trash2, BellPlus, User,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { DateTimePicker } from '../ui/date-picker';
import { Card } from '../ui/card';
import { ConfirmDeleteDialog } from '../ui/confirm-delete-dialog';
import { cn } from '../../lib/cn';
import { useStore } from '../../store/useStore';
import { useAuth } from '../../hooks/useAuth';
import { formatEventAuthor } from '../../lib/eventAuthor';
import { leadService } from '../../lib/leadService';
import {
    LeadEvent, LeadEventType, LEAD_EVENT_TYPE_LABELS,
} from '../../types/leads.types';

const EVENT_ICONS: Record<LeadEventType, React.ElementType> = {
    call: Phone,
    email: Mail,
    note: StickyNote,
    meeting: Users,
    whatsapp: MessageCircle,
    stage_change: ArrowRightCircle,
    conversione: UserCheck,
};

const EVENT_COLORS: Record<LeadEventType, string> = {
    call: 'bg-slate-100 text-primary-600',
    email: 'bg-yellow-100 text-yellow-600',
    note: 'bg-gray-100 text-gray-600',
    meeting: 'bg-purple-100 text-purple-600',
    whatsapp: 'bg-emerald-100 text-emerald-600',
    stage_change: 'bg-violet-100 text-violet-600',
    conversione: 'bg-green-100 text-green-600',
};

// Tipi selezionabili manualmente (stage_change/conversione sono automatici)
const MANUAL_TYPES: LeadEventType[] = ['call', 'email', 'note', 'meeting', 'whatsapp'];

function AddLeadEventForm({ leadId, onAdded, onCancel }: { leadId: string; onAdded: () => void; onCancel: () => void }) {
    const { addNotification } = useStore();
    const [eventType, setEventType] = useState<LeadEventType>('call');
    const [title, setTitle] = useState('');
    const [notes, setNotes] = useState('');
    const [date, setDate] = useState<Date | undefined>(new Date());
    const [withReminder, setWithReminder] = useState(false);
    const [remindAt, setRemindAt] = useState<Date | undefined>(new Date(Date.now() + 24 * 60 * 60 * 1000));
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || !date) {
            addNotification({ type: 'error', title: 'Titolo e data richiesti' });
            return;
        }
        setLoading(true);
        try {
            const event = await leadService.createEvent({
                lead_id: leadId,
                event_type: eventType,
                title: title.trim(),
                notes: notes.trim() || null,
                event_date: date.toISOString(),
                status: 'open',
            });
            if (withReminder && remindAt) {
                await leadService.createReminder({
                    event_id: event.id,
                    remind_at: remindAt.toISOString(),
                    is_completed: false,
                });
            }
            addNotification({ type: 'success', title: 'Attività aggiunta' });
            onAdded();
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row">
                <div className="w-full sm:w-1/3">
                    <Select value={eventType} onValueChange={(v) => setEventType(v as LeadEventType)}>
                        <SelectTrigger><SelectValue placeholder="Tipo" /></SelectTrigger>
                        <SelectContent>
                            {MANUAL_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>{LEAD_EVENT_TYPE_LABELS[t]}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-full sm:w-2/3">
                    <DateTimePicker date={date} setDate={setDate} />
                </div>
            </div>

            <Input placeholder="Titolo attività..." value={title} onChange={(e) => setTitle(e.target.value)} required />
            <Textarea placeholder="Note..." value={notes} onChange={(e) => setNotes(e.target.value)} />

            <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={withReminder} onChange={(e) => setWithReminder(e.target.checked)} />
                    <BellPlus className="w-4 h-4" /> Aggiungi reminder
                </label>
                {withReminder && (
                    <DateTimePicker date={remindAt} setDate={setRemindAt} placeholder="Quando ricordare" />
                )}
            </div>

            <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end [&>button]:w-full sm:[&>button]:w-auto">
                <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>Annulla</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Salvataggio...' : 'Salva'}</Button>
            </div>
        </form>
    );
}

export function LeadTimeline({ leadId }: { leadId: string }) {
    const { addNotification } = useStore();
    const { profile } = useAuth();
    const isAdmin = profile?.role === 'admin';
    const [events, setEvents] = useState<LeadEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [toDelete, setToDelete] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            setEvents(await leadService.getEvents(leadId));
        } catch (error) {
            console.error('Error loading lead events:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [leadId]);

    const handleDelete = async () => {
        if (!toDelete) return;
        setIsDeleting(true);
        try {
            await leadService.deleteEvent(toDelete);
            addNotification({ type: 'success', title: 'Attività eliminata' });
            await load();
        } catch (error) {
            console.error(error);
            addNotification({
                type: 'error',
                title: 'Errore eliminazione',
                message: error instanceof Error ? error.message : undefined,
            });
        } finally {
            setIsDeleting(false);
            setToDelete(null);
        }
    };

    return (
        <div className="flex h-full flex-1 flex-col">
            <ConfirmDeleteDialog
                open={!!toDelete}
                onOpenChange={(open) => !open && setToDelete(null)}
                onConfirm={handleDelete}
                isDeleting={isDeleting}
                title="Elimina Attività"
                description="Sei sicuro di voler eliminare questa attività? L'azione è irreversibile."
            />

            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-primary-100 bg-primary-50 px-4 py-3">
                <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-white">
                        <Clock className="h-4 w-4" />
                    </span>
                    <h2 className="text-sm font-semibold text-gray-900">Attività</h2>
                </div>
                <Button onClick={() => setIsAdding(!isAdding)} variant={isAdding ? 'secondary' : 'default'} size="sm">
                    {isAdding ? 'Annulla' : 'Nuova attività'}
                </Button>
            </div>

            {isAdding && (
                <div className="border-b border-gray-100 px-4 py-4">
                    <AddLeadEventForm
                        leadId={leadId}
                        onAdded={() => { setIsAdding(false); load(); }}
                        onCancel={() => setIsAdding(false)}
                    />
                </div>
            )}

            <div className="relative flex flex-1 flex-col space-y-4 p-4">
                {events.length > 0 && <div className="absolute left-6 top-2 bottom-2 w-0.5 bg-gray-200 -z-10" />}
                {loading ? (
                    <div className="text-center py-4 text-gray-500">Caricamento attività...</div>
                ) : events.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-8 text-center">
                        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
                            <Clock className="h-7 w-7" />
                        </span>
                        <div>
                            <p className="text-sm font-semibold text-gray-900">Nessuna attività</p>
                            <p className="mt-1 max-w-xs text-sm text-gray-500">
                                Registra una chiamata, un messaggio o una nota.
                            </p>
                        </div>
                    </div>
                ) : (
                    events.map((event) => {
                        const Icon = EVENT_ICONS[event.event_type] || Clock;
                        const isExpanded = expandedId === event.id;
                        return (
                            <div key={event.id} className="relative pl-12 sm:pl-14">
                                <div className={cn(
                                    'absolute left-2 top-0 w-8 h-8 rounded-full flex items-center justify-center border-2 border-white shadow-sm z-10',
                                    EVENT_COLORS[event.event_type]
                                )}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <Card
                                    className={cn(
                                        'p-3 hover:shadow-md transition-shadow cursor-pointer border-l-4 group relative',
                                        event.status === 'done' ? 'border-l-green-500' : 'border-l-transparent'
                                    )}
                                    onClick={() => setExpandedId(isExpanded ? null : event.id)}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                                <span className="text-xs font-bold uppercase text-gray-500 tracking-wider">
                                                    {LEAD_EVENT_TYPE_LABELS[event.event_type]}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    {format(new Date(event.event_date), 'd MMM yyyy, HH:mm', { locale: it })}
                                                </span>
                                                <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                                                    <User className="w-3 h-3" />
                                                    {formatEventAuthor(event.creator)}
                                                </span>
                                            </div>
                                            <h4 className="text-sm font-semibold text-gray-900 mt-1 break-words">{event.title}</h4>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {isAdmin && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100"
                                                    onClick={(e) => { e.stopPropagation(); setToDelete(event.id); }}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {event.notes && (
                                                <div className="text-gray-400">
                                                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {isExpanded && event.notes && (
                                        <div className="mt-3 pt-3 border-t text-sm text-gray-600 whitespace-pre-wrap">
                                            {event.notes}
                                        </div>
                                    )}

                                    {event.reminders && event.reminders.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {event.reminders.map((rem) => (
                                                <div key={rem.id} className={cn(
                                                    'text-xs px-2 py-0.5 rounded-full flex items-center gap-1 border',
                                                    rem.is_completed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                                )}>
                                                    <Clock className="w-3 h-3" />
                                                    {format(new Date(rem.remind_at), 'd MMM, HH:mm', { locale: it })}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default LeadTimeline;
